/**
 * Agent Transition Graph for Ameen Insurance Assistant
 * 
 * Defines valid transitions between agents and provides
 * utility functions for determining likely next agents.
 * 
 * Session start: greeting → (by intent) policy | claim | faq
 * Mid-session "main menu": any agent → router_agent (return_to_menu context)
 * Policy flow: greeting|router → policy → document_agent → under_writing_question_agent → vehicle_inspection → submit_policy
 * Claim flow: greeting|router → claim → vehicle_inspection → submit_claim
 * - greeting_agent, router_agent, policy_agent, document_agent, vehicle_inspection_agent,
 * - submit_policy_agent, claim_agent, submit_claim_agent, faq_agent
 */

/**
 * Define valid transitions for each agent
 * Key: source agent ID
 * Value: array of possible target agent IDs
 */
export const AGENT_TRANSITIONS: Record<string, string[]> = {
  // Greeting: present options, then route by intent to policy, claim, or FAQ (not router)
  'greeting_agent': ['policy_agent', 'claim_agent', 'faq_agent'],

  // Router: main menu when user returns mid-session; can go to any main flow
  'router_agent': ['policy_agent', 'claim_agent', 'faq_agent'],

  // Policy goes to document agent (documents then inspection)
  'policy_agent': ['document_agent', 'router_agent', 'claim_agent', 'faq_agent'],

  // Document agent goes to underwriting questions (then underwriting → vehicle inspection)
  'document_agent': ['under_writing_question_agent', 'policy_agent', 'router_agent', 'faq_agent'],

  // Underwriting question agent goes to vehicle inspection or back to policy/router
  'under_writing_question_agent': ['vehicle_inspection_agent', 'policy_agent', 'router_agent', 'faq_agent'],

  // Claim can go to inspection or back to router/policy
  'claim_agent': ['vehicle_inspection_agent', 'router_agent', 'policy_agent', 'faq_agent'],

  // Vehicle inspection forwards to submit_policy_agent (policy flow) or submit_claim_agent (claim flow); user may return to main menu
  'vehicle_inspection_agent': ['submit_policy_agent', 'submit_claim_agent', 'claim_agent', 'router_agent'],

  // Submit Policy Flow (post-inspection); user may choose menu, new policy, or claim
  'submit_policy_agent': ['policy_agent', 'router_agent', 'claim_agent', 'faq_agent'],

  // Submit Claim Flow
  'submit_claim_agent': ['policy_agent', 'router_agent', 'faq_agent'],

  // FAQ can go to any flow
  'faq_agent': ['router_agent', 'policy_agent', 'claim_agent', 'submit_claim_agent', 'submit_policy_agent'],
};

/**
 * Transition likelihood weights (higher = more likely)
 * Used for pre-warming the connection pool
 */
export const TRANSITION_WEIGHTS: Record<string, Record<string, number>> = {
  'greeting_agent': {
    'policy_agent': 0.4,
    'claim_agent': 0.4,
    'faq_agent': 0.2,
  },
  'router_agent': {
    'policy_agent': 0.4,  // Common choice
    'claim_agent': 0.4,   // Common choice
    'faq_agent': 0.2,     // Less common
  },
  'policy_agent': {
    'document_agent': 0.6,   // Required for policy (then document → inspection)
    'router_agent': 0.2,
    'claim_agent': 0.1,
    'faq_agent': 0.1,
  },
  'document_agent': {
    'under_writing_question_agent': 0.6, // After documents
    'policy_agent': 0.2,
    'router_agent': 0.1,
    'faq_agent': 0.1,
  },
  'under_writing_question_agent': {
    'vehicle_inspection_agent': 0.6, // After underwriting questions
    'policy_agent': 0.2,
    'router_agent': 0.1,
    'faq_agent': 0.1,
  },
  'claim_agent': {
    'vehicle_inspection_agent': 0.6, // Required for claim
    'router_agent': 0.2,
    'policy_agent': 0.1,
    'faq_agent': 0.1,
  },
  'vehicle_inspection_agent': {
    'submit_policy_agent': 0.4, // Policy flow (post-inspection)
    'submit_claim_agent': 0.4,   // Claim flow (post-inspection)
    'router_agent': 0.2,        // User returns to main menu
  },
  'submit_policy_agent': {
    'router_agent': 0.5, // User choice: menu
    'policy_agent': 0.25, // User choice: new policy
    'claim_agent': 0.25, // User choice: file claim
  },
  'submit_claim_agent': {
    'router_agent': 0.8, // End of flow
    'policy_agent': 0.2,
  },
  'faq_agent': {
    'router_agent': 0.4,
    'policy_agent': 0.3,
    'claim_agent': 0.3,
  },
};

/**
 * Get likely next agents from current agent
 * Returns agents sorted by transition probability
 * 
 * @param currentAgentId - Current agent ID
 * @param contextHints - Optional hints from current context (e.g., caller_agent)
 * @returns Array of likely next agent IDs, sorted by probability (highest first)
 */
export function getLikelyNextAgents(
  currentAgentId: string,
  contextHints?: { caller_agent?: string }
): string[] {
  const weights = TRANSITION_WEIGHTS[currentAgentId];
  if (!weights) {
    return [];
  }

  // For vehicle_inspection_agent, prioritize the return agent (submit_policy_agent or submit_claim_agent)
  if (currentAgentId === 'vehicle_inspection_agent' && contextHints?.caller_agent) {
    const returnAgent = getVehicleInspectionReturnAgent(contextHints.caller_agent);
    const others = Object.keys(weights).filter(a => a !== returnAgent);
    return [returnAgent, ...others];
  }

  // Sort by weight (descending)
  return Object.entries(weights)
    .sort(([, a], [, b]) => b - a)
    .map(([agentId]) => agentId);
}

/**
 * Check if a transition from source to target is valid
 * 
 * @param sourceAgentId - Source agent ID
 * @param targetAgentId - Target agent ID
 * @returns true if transition is valid
 */
export function isValidTransition(sourceAgentId: string, targetAgentId: string): boolean {
  const validTargets = AGENT_TRANSITIONS[sourceAgentId];
  return validTargets ? validTargets.includes(targetAgentId) : false;
}

/**
 * Get all valid target agents from a source agent
 * 
 * @param sourceAgentId - Source agent ID
 * @returns Array of valid target agent IDs
 */
export function getValidTransitions(sourceAgentId: string): string[] {
  return AGENT_TRANSITIONS[sourceAgentId] || [];
}

/**
 * Get the return agent for vehicle inspection based on caller_agent
 * Policy flow: inspection -> submit_policy_agent (no return to policy_agent)
 * Claim flow: inspection -> submit_claim_agent
 *
 * @param callerAgent - The agent that called vehicle inspection
 * @returns The agent to transition to after inspection
 */
export function getVehicleInspectionReturnAgent(callerAgent: string | undefined): string {
  if (callerAgent === 'policy_agent') return 'submit_policy_agent';
  if (callerAgent === 'under_writing_question_agent') return 'submit_policy_agent'; // Policy flow
  if (callerAgent === 'claim_agent') return 'submit_claim_agent';
  return 'submit_policy_agent';
}
